import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { useMortgageRefinanceState } from 'components/flows/mortgageRefinance/state/useMortgageRefinanceState';
import { MORTGAGE_REFINANCE_BASE_URL } from 'const';
import { useDeleteMortgageRefinanceProcessMutation, useMortgageRefinanceProcessQuery } from 'generated/graphql';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { MoneyhoonProcessPreviewContent } from './MoneyhoonProcessPreviewContent';

export interface MortgageRefinancePreviewDialogProps extends DialogComponentProps<void> {
  id?: string;
}

export const MortgageRefinanceProductPreviewDialog: React.FC<MortgageRefinancePreviewDialogProps> = (props) => {
  const { t } = useTranslation();
  const notify = useNotification();
  const { isAuthenticated } = useContext(AuthContext);
  const { clear, state } = useMortgageRefinanceState();

  const { data } = useMortgageRefinanceProcessQuery({
    skip: !isAuthenticated || !props.id,
    variables: {
      id: props.id ?? '',
    },
  });

  const [deleteProcess, { loading: loadingRemove }] = useDeleteMortgageRefinanceProcessMutation({
    onCompleted: () => {
      props.onResolve();
    },
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['AllProcesses'],
    variables: {
      id: props.id ?? '',
    },
  });

  const handleRemove = async () => {
    if (props.id) {
      await deleteProcess();
    } else {
      clear();
      props.onResolve();
    }
  };

  const navigatePath = props.id
    ? `${MORTGAGE_REFINANCE_BASE_URL}/${props.id}/current-step`
    : `${MORTGAGE_REFINANCE_BASE_URL}/current-step`;

  const progress = isAuthenticated ? data?.process.inquiry?.inquiryProgress : state.meta?.progress;

  const updateTimestamp = isAuthenticated ? data?.process.updateTimestamp : state.meta?.updateTimestamp;

  const accepted = !!data?.process.acceptedProposal?.id;

  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <MoneyhoonProcessPreviewContent
        accepted={accepted}
        color="red"
        loadingRemove={loadingRemove}
        navigatePath={navigatePath}
        onRemoveClick={handleRemove}
        progress={progress}
        title={t('products:cards.refinanceMortgage.title')}
        updateDate={updateTimestamp}
      />
    </ResponsiveSideDialog>
  );
};
